import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Save, X } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { useLabel } from '@/lib/labels';

/** `/settings/employment-types` (雇用形態タブ、v0.2.5 S-11)。既存 /employment-types API を利用。 */
type EmploymentType = { type_id: string; code: string; label: string; is_shift_eligible: boolean; is_labor_law_target: boolean };
type EditingRow = { type_id: string; label: string; is_shift_eligible: boolean; is_labor_law_target: boolean };

export function EmploymentTypesSettingsPage() {
  const t = useLabel();
  const qc = useQueryClient();
  const { data: types = [], isLoading } = useQuery<EmploymentType[]>({ queryKey: ['employment-types'], queryFn: () => apiFetch<EmploymentType[]>('/employment-types') });

  const [creating, setCreating] = useState<{ code: string; label: string; is_shift_eligible: boolean; is_labor_law_target: boolean } | null>(null);
  const [editing, setEditing] = useState<EditingRow | null>(null);
  const [deleting, setDeleting] = useState<EmploymentType | null>(null);

  const createMut = useMutation({
    mutationFn: (body: typeof creating) => apiFetch('/employment-types', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['employment-types'] }); setCreating(null); toast.success('雇用形態を追加しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '追加に失敗しました'),
  });
  const updateMut = useMutation({
    mutationFn: (r: EditingRow) => apiFetch(`/employment-types/${r.type_id}`, {
      method: 'PATCH',
      body: JSON.stringify({ label: r.label.trim(), is_shift_eligible: r.is_shift_eligible, is_labor_law_target: r.is_labor_law_target }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['employment-types'] }); setEditing(null); toast.success('雇用形態を更新しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/employment-types/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['employment-types'] }); setDeleting(null); toast.success('雇用形態を削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleting(null); },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-h2">{t('entity.employment_types', '雇用形態')}</h2>
          <p className="text-sm text-neutral-sub mt-1">正社員・アルバイト等、雇用形態マスタを管理します。</p>
        </div>
        <button type="button" onClick={() => setCreating({ code: '', label: '', is_shift_eligible: false, is_labor_law_target: true })}
          className="flex items-center gap-1 px-3 py-2 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover whitespace-nowrap">
          <Plus size={16} /> 雇用形態追加
        </button>
      </div>

      {isLoading ? <div className="text-neutral-sub">読み込み中…</div> : (
        <div className="border border-neutral-line rounded-lg bg-white overflow-x-auto">
          <table className="w-full min-w-[620px] text-sm">
            <thead className="bg-neutral-panel text-neutral-sub">
              <tr>
                <th className="text-left px-4 py-2 w-28">{t('field.employment_types.code', 'コード')}</th>
                <th className="text-left px-4 py-2">{t('field.employment_types.label', '名称')}</th>
                <th className="text-center px-4 py-2 w-28">{t('field.employment_types.is_shift_eligible', 'シフト対象')}</th>
                <th className="text-center px-4 py-2 w-28">{t('field.employment_types.is_labor_law_target', '労働法対象')}</th>
                <th className="w-24 px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {types.map((t) => {
                const isEditing = editing?.type_id === t.type_id;
                return (
                  <tr key={t.type_id} className="border-t border-neutral-line">
                    <td className="px-4 py-2 font-mono text-xs text-neutral-sub">{t.code}</td>
                    <td className="px-4 py-2">
                      {isEditing ? (
                        <input value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                          className="w-full px-2 py-1 rounded border border-neutral-line focus-ring" />
                      ) : t.label}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <input type="checkbox" checked={isEditing ? editing.is_shift_eligible : t.is_shift_eligible} disabled={!isEditing}
                        onChange={(e) => isEditing && setEditing({ ...editing, is_shift_eligible: e.target.checked })} />
                    </td>
                    <td className="px-4 py-2 text-center">
                      <input type="checkbox" checked={isEditing ? editing.is_labor_law_target : t.is_labor_law_target} disabled={!isEditing}
                        onChange={(e) => isEditing && setEditing({ ...editing, is_labor_law_target: e.target.checked })} />
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {isEditing ? (
                        <>
                          <button type="button" aria-label="保存" onClick={() => updateMut.mutate(editing)}
                            disabled={updateMut.isPending || editing.label.trim() === ''}
                            className="p-1 text-brand-red hover:text-brand-red-hover disabled:opacity-40"><Save size={14} /></button>
                          <button type="button" aria-label="キャンセル" onClick={() => setEditing(null)}
                            className="ml-1 p-1 text-neutral-sub hover:text-neutral-text"><X size={14} /></button>
                        </>
                      ) : (
                        <>
                          <button type="button" aria-label="編集"
                            onClick={() => setEditing({ type_id: t.type_id, label: t.label, is_shift_eligible: t.is_shift_eligible, is_labor_law_target: t.is_labor_law_target })}
                            className="p-1 text-brand-red hover:text-brand-red-hover"><Pencil size={14} /></button>
                          <button type="button" aria-label="削除" onClick={() => setDeleting(t)}
                            className="ml-1 p-1 text-neutral-sub hover:text-warning-text"><Trash2 size={14} /></button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {types.length === 0 && <EmptyState message="雇用形態が登録されていません" description="「雇用形態追加」から追加してください" />}
        </div>
      )}

      {/* Design MAJOR対応 (2026-08-03レビュー): 自作の fixed/overlay div ではなく共有Modal を使う */}
      <Modal
        open={creating !== null}
        title="雇用形態追加"
        onClose={() => setCreating(null)}
        size="sm"
        footer={creating && (
          <>
            <button type="button" onClick={() => setCreating(null)} className="px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel">キャンセル</button>
            <button type="submit" form="employment-type-create-form" disabled={createMut.isPending} className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover disabled:opacity-50">
              {createMut.isPending ? '作成中…' : '作成'}
            </button>
          </>
        )}
      >
        {creating && (
          <form id="employment-type-create-form"
            onSubmit={(e) => {
              e.preventDefault();
              const code = creating.code.trim();
              const label = creating.label.trim();
              if (!code) { toast.error('コードを入力してください'); return; }
              if (!label) { toast.error('名称を入力してください'); return; }
              createMut.mutate({ ...creating, code, label });
            }}
            className="space-y-3">
            <div>
              <label className="block text-sm text-neutral-sub mb-1">{t('field.employment_types.code', 'コード')}*</label>
              <input required autoFocus value={creating.code} onChange={(e) => setCreating({ ...creating, code: e.target.value })}
                pattern="[A-Za-z0-9_-]+" className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">{t('field.employment_types.label', '名称')}*</label>
              <input required value={creating.label} onChange={(e) => setCreating({ ...creating, label: e.target.value })}
                placeholder="例：正社員、アルバイト" className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={creating.is_shift_eligible}
                onChange={(e) => setCreating({ ...creating, is_shift_eligible: e.target.checked })} />
              {t('field.employment_types.is_shift_eligible', 'シフト管理対象')}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={creating.is_labor_law_target}
                onChange={(e) => setCreating({ ...creating, is_labor_law_target: e.target.checked })} />
              {t('field.employment_types.is_labor_law_target', '労働法規制対象')}
            </label>
          </form>
        )}
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        title="雇用形態を削除しますか？"
        destructive
        confirmLabel="削除する"
        loading={deleteMut.isPending}
        onClose={() => { if (!deleteMut.isPending) setDeleting(null); }}
        onConfirm={() => { if (deleting) deleteMut.mutate(deleting.type_id); }}
        message={<p className="text-sm">既に社員に使用されている雇用形態は削除できません (サーバー側でチェックされます)。</p>}
      />
    </div>
  );
}
